import { WorkoutLevelEnum } from 'types/enums';

import { WorkoutResultWithSets, WorkoutSectionWithScoring } from './types';

export const sectionHasLevels = (
  section: Pick<WorkoutSectionWithScoring, 'levelScaled' | 'levelRx' | 'levelRxPlus'>,
): boolean => section.levelScaled || section.levelRx || section.levelRxPlus;

// Decision 3 (implementation-details.md): the backend requires an explicit level
// whenever the section offers one — this is the UI's preselection order,
// RX -> Scaled -> RX+, first one the section offers. Not a server default.
export const getDefaultLevel = (
  section: Pick<WorkoutSectionWithScoring, 'levelScaled' | 'levelRx' | 'levelRxPlus'>,
): WorkoutLevelEnum | null => {
  if (section.levelRx) return WorkoutLevelEnum.RX;
  if (section.levelScaled) return WorkoutLevelEnum.SCALED;
  if (section.levelRxPlus) return WorkoutLevelEnum.RX_PLUS;
  return null;
};

// A leveled section is always score_entire_section (exactly one movement), but this
// checks all of section.movements rather than assuming that shape.
export const sectionHasServerResult = (
  section: Pick<WorkoutSectionWithScoring, 'movements'>,
  apiResults: WorkoutResultWithSets[],
): boolean =>
  section.movements.some(m =>
    apiResults.some(r => r.workoutSectionMovementId === m.workoutSectionMovementId),
  );

// Seeds per-section level state: the level already on a logged result if one exists
// (every row in a section carries the same value, kept in sync server-side), else
// the UI's default pick.
export const buildInitialLevels = (
  sections: WorkoutSectionWithScoring[],
  apiResults: WorkoutResultWithSets[] = [],
): Record<string, WorkoutLevelEnum | null> => {
  const state: Record<string, WorkoutLevelEnum | null> = {};
  sections.filter(sectionHasLevels).forEach(section => {
    const existing = apiResults.find(r =>
      section.movements.some(m => m.workoutSectionMovementId === r.workoutSectionMovementId),
    );
    state[section.workoutSectionId] = existing
      ? (existing.level as WorkoutLevelEnum) || null
      : getDefaultLevel(section);
  });
  return state;
};
